import { LineRecord, NodeState, SetOperation } from "@/components/FlowSheetEditor/services/modal"
import { getSimUnitData } from "@/services/project/simulation.service"
import { EquipmentType, NozzleDirection } from "@/types/common"

export interface IManualInputVar {
  label: string;
  value: string;
  dimCode?: string
}

export const StreamCompositionKey = ':OUT:xi_$1'

const streamAttributes: IManualInputVar[] = [
  {label: 'Molar Flow', value: ':OUT:F', dimCode: 'MOL_F'},
  {label: 'Mass Flow', value: ':OUT:M', dimCode: 'MASS_F'},
  {label: 'Pressure', value: ':OUT:P', dimCode: 'PRES'},
  {label: 'Temperature', value: ':OUT:T', dimCode: 'TEMP'},
  {label: 'Vapor Fraction', value: ':OUT:VF', dimCode: 'PF'},
  {label: 'Enthalpy', value: ':OUT:h', dimCode: 'MOL_H'},
  {label: 'Entropy', value: ':OUT:s', dimCode: 'MOL_S'},
  {label: 'Composition', value: ':OUT:xi_$1', dimCode: 'MOL_FRAC'},
  {label: 'Heat Input', value: ':UNIT:Q', dimCode: 'HEATFLOW'},
  {label: 'Pressure Drop', value: ':UNIT:DP', dimCode: 'DPRES'},
  {label: 'Temperature Delta', value: ':UNIT:DT', dimCode: 'DTEMP'},
  {label: 'Entropy Delta', value: ':UNIT:ds', dimCode: 'MOL_S'},
]

const baseUnitAttributes: IManualInputVar[] = [
  {label: 'Flow OUT 0', value: ':OUT0:ETA'},
  {label: 'Flow OUT 1', value: ':OUT1:ETA'},
]

const reactorAttributes: IManualInputVar[] = [
  {label: 'Heat Input', value: ':Q:Q'},
]

const SetOperationAttributes: IManualInputVar[] = [
  {label: 'Factor', value: ':UNIT:FACTOR'},
  {label: 'Offset', value: ':UNIT:OFFSET'},
]

export function getUnitAttributes(unit: NodeState | LineRecord | SetOperation): IManualInputVar[] {
  if (!unit) return []
  if ((unit as NodeState).equipment) {
    const unitState = unit as NodeState
    const equipment = (unit as NodeState).equipment
    const simArgs = (unit as NodeState).simArgs || {}
    switch (equipment.type) {
      case EquipmentType.HeatExchanger: {
        if (simArgs.S1_BP) {
          return [
            {label: 'Stream 1 BP Temperature', dimCode: 'TEMP', value: ':STR1:OUT:T'},
            {label: 'Stream 2 BP Temperature', dimCode: 'TEMP', value: ':STR2:OUT:T'},
            {label: 'Stream 1 Heat Input', dimCode: 'HEATFLOW', value: ':STR1:UNIT:Q'},
            {label: 'Stream 2 Heat Input', dimCode: 'HEATFLOW', value: ':STR2:UNIT:Q'},
          ]
        }
        return [
          {label: 'Stream 1 Heat Input', dimCode: 'HEATFLOW', value: ':STR1:UNIT:Q'},
          {label: 'Stream 2 Heat Input', dimCode: 'HEATFLOW', value: ':STR2:UNIT:Q'},
        ]
      }
      case EquipmentType.StreamSplitter: {
//        return unitState.nozzles.filter(item => item.direction === NozzleDirection.Outlet)
//          .map((outNozzle, index) => {
//            return {label: `Flow OUT ${index}`, value: `:OUT${index}:ETA`}
//          })
          const etaNodes =  unitState.nozzles.filter(item => item.direction === NozzleDirection.Outlet)
          .map((outNozzle, index) => {
            return {label: `Split ${index}`, value: `:OUT${index}:ETA`}
          })
          const flowNodes = unitState.nozzles.filter(item => item.direction === NozzleDirection.Outlet)
          .map((outNozzle, index) => {
            return {label: `Flow ${index}`, value: `:OUT${index}:F`, dimCode: 'MOL_F'}
          })
          return [...etaNodes, ...flowNodes]

      }
      case EquipmentType.StreamMixer: {
        return unitState.nozzles.filter(item => item.direction === NozzleDirection.Inlet)
          .map((outNozzle, index) => {
            return {label: `Flow In ${index}`, value: `:IN${index}:ETA`}
          })
      }
      case EquipmentType.Deaerator: {
        const componentsData = []
        return [
          {label: 'Heat Input', value: ':LIQUID_SPLITTER:UNIT:Q'},
          {label: `Split Factor 0`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[0]`},
          {label: `Split Factor 1`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[1]`},
          {label: `Split Factor 2`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[2]`},
          {label: `Split Factor 3`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[3]`},
          {label: `Split Factor 4`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[4]`},
          {label: `Split Factor 5`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[5]`},
          {label: `Split Factor 6`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[6]`},
          {label: `Split Factor 7`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[7]`},
          {label: `Split Factor 8`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[8]`},
          {label: `Split Factor 9`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[9]`},
          {label: `Split Factor 10`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[10]`},
          {label: `Split Factor 11`, value: `:LIQUID_SPLITTER:UNIT:SPLIT_$1[11]`}
        ]
      }
      case EquipmentType.Reactor: {
        return [
          {label: 'Heat Input', value: ':Q:Q'},
        ]
      }
      case EquipmentType.Burner: {
        return [
          {label: 'Heat Input', value: ':FLAME:Q:Q'},
        ]
      }
      case EquipmentType.SteamDrum: {
        const etaNodes =  unitState.customNozzles!.filter(item => item.name.startsWith('LIQ_MANIFOLD:OUT'))
          .map((outNozzle, index) => {
            return {label: `Liquid Split ${outNozzle.name.substring(16)}`, value: `:OUT${outNozzle.name.substring(16)}:ETA`}
          })
          const flowNodes =  unitState.customNozzles!.filter(item => item.name.startsWith('LIQ_MANIFOLD:OUT'))
          .map((outNozzle, index) => {
            return {label: `Liquid Flow ${outNozzle.name.substring(16)}`, value: `:LIQ_MANIFOLD:OUT${outNozzle.name.substring(16)}:F`, dimCode: 'MOL_F'}
          })
          return [...etaNodes, ...flowNodes]
      }
      default:
        return baseUnitAttributes
    }
  } else if ((unit as SetOperation).sourceUnit !== undefined) {
    return SetOperationAttributes
  }
  return streamAttributes
}

export async function getUnitAttributesWithComponents(unit: NodeState, simId: string) {
  const baseAttributes = getUnitAttributes(unit) 
  const unitData = await getSimUnitData({sId: simId, id: unit.id})
}